import { KText } from '@/ui-library/components';
import { Flex, Box, Icon, Tooltip } from '@chakra-ui/react';
import { Copy03 } from '@kula-ai/icons/dual-tone/Copy03';
import { Copy03 as Copy03Outline } from '@kula-ai/icons/outline/Copy03';
import { useState } from 'react';
import { CheckCircle } from '@kula-ai/icons/outline/CheckCircle';

export const StatusCell = (row, formattedRecordsTable) => {
  const isMissing = row?.original?.Valid === 'unknown';
  return (
    <KText
      size="sm-medium"
      color={isMissing ? 'pastel-1-dark' : 'pastel-2-dark'}
      background={isMissing ? 'pastel-1-light' : 'pastel-2-light'}
      borderRadius="4px"
      p="2px 8px"
      alignSelf="start"
    >
      {isMissing ? 'Missing' : 'Verified'}
    </KText>
  );
};

export const TypeCell = (row: any) => {
  return (
    <KText
      size="sm-medium"
      color="secondary-900"
      textTransform={'uppercase'}
      alignSelf="start"
    >
      {row?.original?.Type}
    </KText>
  );
};

export const HostCell = (row) => {
  const host = row?.original?.Host;
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = () => {
    if (!host) return;

    navigator.clipboard.writeText(host);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };
  return (
    <Flex alignSelf="start" width="100%" justifyContent="space-between">
      <KText
        flex="1"
        size="sm-medium"
        color="secondary-900"
        w="220px"
        alignItems="start"
      >
        {host || '-'}
      </KText>
      {host && (
        <Box
          ml="24px"
          border="2px solid"
          borderColor={'border-color'}
          borderRadius="6px"
          p="8px"
          onClick={handleCopy}
          cursor="pointer"
          position="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Tooltip
            label={copied ? 'Copied to clipboard' : 'Copy'}
            placement="right"
            bg="gray.700"
            color="white"
            fontSize="12px"
            borderRadius="4px"
            shouldWrapChildren
            isOpen={copied || undefined}
            hasArrow
          >
            <Icon
              as={copied ? CheckCircle : isHovered ? Copy03Outline : Copy03}
              w="16px"
              h="16px"
              color={copied ? 'success-200' : 'inherit'}
            />
          </Tooltip>
        </Box>
      )}
    </Flex>
  );
};

export const RequiredValueCell = (row) => {
  const [copied, setCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const handleCopy = () => {
    if (!reqValue) return;

    navigator.clipboard.writeText(reqValue);
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };
  const reqValue = row?.original?.RequiredValue;
  return (
    <Flex alignItems="start" width="100%" justifyContent="space-between">
      <KText flex="1" size="sm-medium" color="secondary-900" w="280px">
        {reqValue || '-'}
      </KText>
      <Box
        ml="24px"
        border="2px solid"
        borderColor={'border-color'}
        borderRadius="6px"
        p="8px"
        onClick={handleCopy}
        cursor="pointer"
        position="relative"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Tooltip
          label={copied ? 'Copied to clipboard' : 'Copy'}
          placement="left"
          bg="gray.700"
          color="white"
          hasArrow
          isOpen={copied || undefined}
          fontSize="12px"
          borderRadius="4px"
          shouldWrapChildren
        >
          <Icon
            as={copied ? CheckCircle : isHovered ? Copy03Outline : Copy03}
            w="16px"
            h="16px"
            color={copied ? 'success-200' : 'inherit'}
          />
        </Tooltip>
      </Box>
    </Flex>
  );
};

export const Header = ({ title = '' }) => {
  return (
    <KText
      w="fit-content"
      size="sm-bold"
      color="secondary-900"
      textTransform={'capitalize'}
    >
      {title}
    </KText>
  );
};

export const useDNSConfig = ({ formattedRecordsTable = [] }) => {
  const getColumnConfig = {
    StatusCell: {
      Header: Header({ title: 'Status' }),
      maxWidth: 50,
      isRowSelectable: true,
      style: { display: 'flex', alignItems: 'start' },
      accessor: 'Status',
      Cell: ({ row, formattedRecordsTable }) =>
        StatusCell(row, formattedRecordsTable),
    },
    TypeCell: {
      Header: Header({ title: 'Type' }),
      maxWidth: 50,
      isRowSelectable: true,
      accessor: 'type',
      Cell: ({ row }) => TypeCell(row),
    },
    HostCell: {
      Header: Header({ title: 'Host' }),
      maxWidth: 120,
      accessor: 'host',
      Cell: ({ row }) => HostCell(row),
    },
    RequiredValueCell: {
      Header: Header({ title: 'Required value' }),
      maxWidth: 150,
      accessor: 'required value',
      Cell: ({ row }) => RequiredValueCell(row),
    },
  };

  return { getColumnConfig };
};
